import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';
import { environment } from 'src/environments/environment';

interface BidItem {
  item_id: number;
  item_code: string;
  item_description: string;
  unit: string;
  estimated_quantity: number;  // BOQ original qty
  quantity: number;            // Bidder's qty (editable)
  unit_rate: number;           // BOQ estimated rate
  bid_rate: number;            // Bidder's unit rate (editable)
  line_total: number;          // quantity * bid_rate
  resources?: BidderResource[]; // Optional bidder resource inputs
}

interface BidderResource {
  type: 'man' | 'machine' | 'material';
  description: string;
  quantity: number;
  unit?: string;
  unit_rate: number;
}

@Component({
  selector: 'app-boq-bidding-invite',
  templateUrl: './boq-bidding-invite.component.html',
  styleUrls: ['./boq-bidding-invite.component.css']
})
export class BoqBiddingInviteComponent implements OnInit {
  token: string | null = null;
  data: any = null;
  bidClosed = false;
  loading = true;
  error: string | null = null;
  bidForm: FormGroup;
  guestBidForm: FormGroup;
  bidItems: BidItem[] = [];
  useItemWiseBid = true;
  submitting = false;
  expandedResourceItems: Set<number> = new Set(); // item_ids with resources section expanded
  bidSubmitted = false;
  isAuthenticated = false;
  existingBid: any = null;
  existingGuestBid: any = null;
  /** Logged-in org was invited (org id or matching contact email). */
  isOrgInvited = false;
  /** Guest email form is only for users who are not logged in. */
  showGuestForm = false;
  bidderRole: 'invited' | 'planner' | 'not_invited' | null = null;
  bidderOrganization: { org_id?: number; org_name?: string | null; email?: string | null } | null = null;
  bidderViewLoaded = false;
  queries: any[] = [];
  queryText = '';
  submittingQuery = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApiService,
    private toastr: ToastrMsgService,
    private fb: FormBuilder
  ) {
    this.bidForm = this.fb.group({
      bid_amount: [null, [Validators.required, Validators.min(0)]],
      remarks: ['']
    });
    this.guestBidForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      contact_name: [''],
      bid_amount: [null, [Validators.min(0)]],
      remarks: ['']
    });
  }

  canEditBid(): boolean {
    if (this.bidClosed || this.bidSubmitted) return false;
    if (!this.isAuthenticated) return true;
    return this.isOrgInvited;
  }

  ngOnInit(): void {
    this.token = this.route.snapshot.paramMap.get('token');
    this.isAuthenticated = this.apiService.isAuthenticated();
    const emailParam = this.route.snapshot.queryParamMap.get('email');
    if (emailParam) {
      this.guestBidForm.patchValue({ email: emailParam });
    }

    if (!this.token) {
      this.error = 'Invalid invitation link';
      this.loading = false;
      return;
    }

    this.loadInvitation();
  }

  loadInvitation(): void {
    this.apiService.getBiddingInvitationByToken(this.token!).subscribe({
      next: (res: any) => {
        if (res.success && res.data) {
          this.data = res.data;
          this.bidClosed = !!res.bid_closed;
          if (!this.bidClosed) {
            this.initBidItems();
            this.updateTotalFromItems();
            if (this.isAuthenticated) {
              this.loadBidderView();
            } else {
              this.showGuestForm = true;
              const emailParam = this.route.snapshot.queryParamMap.get('email');
              if (emailParam) {
                this.loadGuestBid(emailParam);
              }
            }
          }
        } else {
          this.error = res.error || 'Failed to load invitation';
        }
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.error || 'Invitation not found or expired';
        this.loading = false;
      }
    });
  }

  loadGuestBid(email: string): void {
    this.apiService.getGuestBid(this.token!, email).subscribe({
      next: (res: any) => {
        if (res.success && res.data?.existing_bid) {
          this.existingGuestBid = res.data.existing_bid;
          this.guestBidForm.patchValue({
            email: res.data.existing_bid.email,
            contact_name: res.data.existing_bid.contact_name || '',
            bid_amount: res.data.existing_bid.bid_amount,
            remarks: res.data.existing_bid.remarks || ''
          });
          if (res.data.existing_bid.bid_data?.items?.length) {
            res.data.existing_bid.bid_data.items.forEach((bi: any) => {
              const item = this.bidItems.find(b => b.item_id === bi.item_id);
              if (item) {
                item.quantity = bi.quantity != null ? parseFloat(bi.quantity) : item.quantity;
                item.bid_rate = bi.bid_rate != null ? parseFloat(bi.bid_rate) : item.unit_rate;
                item.line_total = (item.quantity || 0) * (item.bid_rate || 0);
                if (bi.resources?.length) item.resources = bi.resources;
              }
            });
            this.updateTotalFromItems();
          }
        }
      },
      error: () => { /* Not invited or no bid yet */ }
    });
  }

  loadBidderView(): void {
    this.showGuestForm = false;
    this.apiService.getBidderView(this.token!).subscribe({
      next: (res: any) => {
        this.bidderViewLoaded = true;
        if (res.success && res.data) {
          const role = res.data.role === 'planner' ? 'planner'
            : (res.data.role === 'invited' || res.data.is_org_invited) ? 'invited'
            : 'not_invited';
          this.bidderRole = role;
          this.isOrgInvited = role === 'invited';
          this.bidderOrganization = res.data.organization || null;
          this.existingBid = res.data.existing_bid;
          this.queries = res.data.queries || [];
          if (this.isOrgInvited) {
            this.patchBidFormFromExisting(this.existingBid);
          }
        } else {
          this.bidderRole = 'not_invited';
          this.isOrgInvited = false;
        }
      },
      error: () => {
        this.bidderViewLoaded = true;
        this.isOrgInvited = false;
        this.showGuestForm = false;
        this.bidderRole = 'not_invited';
        this.bidderOrganization = null;
      }
    });
  }

  private patchBidFormFromExisting(bid: any | null): void {
    if (!bid) return;
    this.bidForm.patchValue({
      bid_amount: bid.bid_amount,
      remarks: bid.remarks || ''
    });
    if (bid.bid_data?.items?.length) {
      bid.bid_data.items.forEach((bi: any) => {
        const item = this.bidItems.find(b => b.item_id === bi.item_id);
        if (item) {
          item.quantity = bi.quantity != null ? parseFloat(bi.quantity) : item.quantity;
          item.bid_rate = bi.bid_rate != null ? parseFloat(bi.bid_rate) : item.unit_rate;
          item.line_total = (item.quantity || 0) * (item.bid_rate || 0);
          if (bi.resources?.length) item.resources = bi.resources;
        }
      });
      this.updateTotalFromItems();
    }
  }

  initBidItems(): void {
    if (!this.data?.boq_items) return;
    this.bidItems = this.data.boq_items.map((item: any) => {
      const qty = parseFloat(item.quantity) || 0;
      const rate = parseFloat(item.unit_rate) || 0;
      return {
        item_id: item.item_id,
        item_code: item.item_code,
        item_description: item.item_description,
        unit: item.unit,
        estimated_quantity: qty,
        quantity: 0 as number,       // Don't show estimated qty to bidder; they enter their own
        unit_rate: rate,
        bid_rate: 0 as number,       // Don't show estimated rate to bidder; they enter their own
        line_total: 0,
        resources: [] as BidderResource[]
      };
    });
  }

  toggleResourceExpand(itemId: number): void {
    if (this.expandedResourceItems.has(itemId)) {
      this.expandedResourceItems.delete(itemId);
    } else {
      this.expandedResourceItems.add(itemId);
    }
    this.expandedResourceItems = new Set(this.expandedResourceItems);
  }

  isResourceExpanded(itemId: number): boolean {
    return this.expandedResourceItems.has(itemId);
  }

  addBidderResource(item: BidItem): void {
    if (!item.resources) item.resources = [];
    item.resources.push({ type: 'material', description: '', quantity: 0, unit_rate: 0 });
  }

  removeBidderResource(item: BidItem, resource: BidderResource): void {
    if (item.resources) {
      const idx = item.resources.indexOf(resource);
      if (idx >= 0) item.resources.splice(idx, 1);
    }
  }

  onBidRateChange(item: BidItem): void {
    item.line_total = (item.quantity || 0) * (item.bid_rate || 0);
    this.updateTotalFromItems();
  }

  onBidQtyChange(item: BidItem): void {
    item.line_total = (item.quantity || 0) * (item.bid_rate || 0);
    this.updateTotalFromItems();
  }

  updateTotalFromItems(): void {
    const total = this.bidItems.reduce((sum, i) => sum + (i.line_total || 0), 0);
    this.bidForm.patchValue({ bid_amount: total }, { emitEvent: false });
    this.guestBidForm.patchValue({ bid_amount: total }, { emitEvent: false });
  }

  goToLogin(): void {
    this.router.navigate(['/login'], { queryParams: { returnUrl: this.router.url } });
  }

  goToRegisterToBid(): void {
    const returnUrl = encodeURIComponent(this.router.url.startsWith('/') ? this.router.url : '/' + this.router.url);
    this.router.navigate(['/onboarding/signup-with-email'], { queryParams: { returnUrl } });
  }

  raiseQuery(): void {
    if (!this.queryText.trim()) return;
    this.submittingQuery = true;
    this.apiService.createBiddingQuery(this.token!, this.queryText.trim()).subscribe({
      next: (res: any) => {
        this.submittingQuery = false;
        this.queryText = '';
        this.queries = [res.data, ...this.queries];
        this.toastr.success('Query submitted');
      },
      error: (err) => {
        this.submittingQuery = false;
        this.toastr.error(err.error?.error || 'Failed to submit query');
      }
    });
  }

  onSubmitGuest(): void {
    if (this.isAuthenticated) {
      if (this.isOrgInvited) {
        this.onSubmit();
      }
      return;
    }
    if (this.guestBidForm.invalid) {
      this.guestBidForm.markAllAsTouched();
      return;
    }
    const email = this.guestBidForm.get('email')?.value?.trim();
    const contactName = this.guestBidForm.get('contact_name')?.value?.trim();
    const bidAmount = this.useItemWiseBid
      ? this.bidItems.reduce((sum, i) => sum + (i.line_total || 0), 0)
      : this.guestBidForm.get('bid_amount')?.value;
    if (!bidAmount || bidAmount < 0) {
      this.toastr.error('Please enter a valid bid amount');
      return;
    }
    const bidData = this.useItemWiseBid
      ? { items: this.bidItems.map(i => ({
          item_id: i.item_id,
          item_code: i.item_code,
          item_description: i.item_description,
          unit: i.unit,
          estimated_quantity: i.estimated_quantity,
          estimated_unit_rate: i.unit_rate,
          quantity: i.quantity,
          bid_rate: i.bid_rate,
          line_total: (i.quantity || 0) * (i.bid_rate || 0),
          resources: (i.resources || []).filter(r => r.description?.trim()).map(r => ({
            type: r.type,
            description: r.description?.trim(),
            quantity: parseFloat(String(r.quantity)) || 0,
            unit: r.unit || undefined,
            unit_rate: parseFloat(String(r.unit_rate)) || 0
          }))
        })) }
      : {};
    this.submitting = true;
    this.apiService.submitBiddingResponseGuest(this.token!, {
      email,
      contact_name: contactName || undefined,
      bid_amount: bidAmount,
      bid_data: Object.keys(bidData).length ? bidData : undefined,
      remarks: this.guestBidForm.get('remarks')?.value || undefined
    }).subscribe({
      next: () => {
        this.submitting = false;
        this.bidSubmitted = true;
        this.existingGuestBid = { bid_amount: bidAmount, bid_data: bidData };
        this.toastr.success('Bid submitted successfully');
      },
      error: (err) => {
        this.submitting = false;
        this.toastr.error(err.error?.error || 'Failed to submit bid');
      }
    });
  }

  onSubmit(): void {
    if (!this.isAuthenticated || !this.isOrgInvited) {
      this.toastr.error('Your organization is not invited to submit a bid');
      return;
    }

    const bidAmount = this.useItemWiseBid
      ? this.bidItems.reduce((sum, i) => sum + (i.line_total || 0), 0)
      : this.bidForm.get('bid_amount')?.value;

    if (!bidAmount || bidAmount < 0) {
      this.bidForm.markAllAsTouched();
      this.toastr.error('Please enter a valid bid amount');
      return;
    }

    const bidData = this.useItemWiseBid
      ? { items: this.bidItems.map(i => ({
          item_id: i.item_id,
          item_code: i.item_code,
          item_description: i.item_description,
          unit: i.unit,
          estimated_quantity: i.estimated_quantity,
          estimated_unit_rate: i.unit_rate,
          quantity: i.quantity,
          bid_rate: i.bid_rate,
          line_total: (i.quantity || 0) * (i.bid_rate || 0),
          resources: (i.resources || []).filter(r => r.description?.trim()).map(r => ({
            type: r.type,
            description: r.description?.trim(),
            quantity: parseFloat(String(r.quantity)) || 0,
            unit: r.unit || undefined,
            unit_rate: parseFloat(String(r.unit_rate)) || 0
          }))
        })) }
      : {};

    this.submitting = true;
    this.apiService.submitBiddingResponse(this.token!, {
      bid_amount: bidAmount,
      bid_data: Object.keys(bidData).length ? bidData : undefined,
      remarks: this.bidForm.get('remarks')?.value || undefined
    }).subscribe({
      next: () => {
        this.submitting = false;
        this.bidSubmitted = true;
        this.existingBid = { bid_amount: bidAmount, bid_data: bidData };
        this.toastr.success('Bid submitted successfully');
      },
      error: (err) => {
        this.submitting = false;
        this.toastr.error(err.error?.error || 'Failed to submit bid');
      }
    });
  }

  formatCurrency(amount: number, currency: string = 'INR'): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency
    }).format(amount || 0);
  }

  getDocumentUrl(path: string): string {
    if (!path) return '#';
    if (path.startsWith('http')) return path;
    const base = (environment.imageBaseUrl || '').replace(/\/?$/, '/');
    if (!base) return path;
    return path.startsWith('/') ? base + path.slice(1) : base + path;
  }

  getResourceDescriptions(item: BidItem): string {
    const resources = item.resources || [];
    const descriptions = resources
      .map((r) => r.description)
      .filter((d) => !!d);
    return descriptions.join(', ') || 'None';
  }

  onBidTypeChange(): void {
    if (this.useItemWiseBid) {
      this.updateTotalFromItems();
    }
  }
}
